import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const EMPTY = { player_name: "", jersey_number: "", position: "", grade: "", status: "active", graduation_year: "", ppg_goal: 12, reb_goal: 6, ast_goal: 4 };

export default function PlayerEditDialog({ open, player, onClose, onSaved }) {
  const isNew = !player;
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    if (!open) return;
    if (player) setForm({
      player_name: player.player_name,
      jersey_number: player.jersey_number ?? "",
      position: player.position || "",
      grade: player.grade || "",
      status: player.status || "active",
      graduation_year: player.graduation_year ?? "",
      ppg_goal: player.goals?.ppg ?? 12,
      reb_goal: player.goals?.reb ?? 6,
      ast_goal: player.goals?.ast ?? 4,
    });
    else setForm(EMPTY);
  }, [player, open]);

  const submit = async (e) => {
    e.preventDefault();
    const body = {
      player_name: form.player_name,
      jersey_number: form.jersey_number ? +form.jersey_number : null,
      position: form.position || null,
      grade: form.grade || null,
      status: form.status,
      graduation_year: form.graduation_year ? +form.graduation_year : null,
      goals: { ppg: +form.ppg_goal || 0, reb: +form.reb_goal || 0, ast: +form.ast_goal || 0 },
    };
    try {
      if (isNew) {
        await api.post("/players", body);
        toast.success("Player added");
      } else {
        await api.put(`/players/${player.id}`, body);
        toast.success("Player updated");
      }
      onSaved();
    } catch (err) { toast.error(formatError(err)); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-ink-900 border-ink-700 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="heading-3">{isNew ? "Add Player" : "Edit Player"}</DialogTitle>
          <DialogDescription className="text-ink-300">
            Update name, jersey, position, status, graduation year, and season goals. Stats are preserved.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="player-form">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="eyebrow">Full Name</Label>
              <Input value={form.player_name || ""} onChange={(e) => setForm({ ...form, player_name: e.target.value })} required className="bg-ink-800 border-ink-700 mt-2" data-testid="player-form-name" />
            </div>
            <div>
              <Label className="eyebrow">Jersey #</Label>
              <Input value={form.jersey_number || ""} onChange={(e) => setForm({ ...form, jersey_number: e.target.value })} inputMode="numeric" className="bg-ink-800 border-ink-700 mt-2" />
            </div>
            <div>
              <Label className="eyebrow">Position</Label>
              <Input value={form.position || ""} onChange={(e) => setForm({ ...form, position: e.target.value })} placeholder="PG / SG / SF / PF / C" className="bg-ink-800 border-ink-700 mt-2" />
            </div>
            <div>
              <Label className="eyebrow">Grade</Label>
              <Input value={form.grade || ""} onChange={(e) => setForm({ ...form, grade: e.target.value })} placeholder="9 / 10 / 11 / 12" className="bg-ink-800 border-ink-700 mt-2" />
            </div>
            <div>
              <Label className="eyebrow">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="bg-ink-800 border-ink-700 mt-2" data-testid="player-form-status"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-ink-900 border-ink-700">
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="graduated">Graduated</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="eyebrow">Graduation Year</Label>
              <Input value={form.graduation_year || ""} onChange={(e) => setForm({ ...form, graduation_year: e.target.value })} inputMode="numeric" placeholder="e.g. 2026" className="bg-ink-800 border-ink-700 mt-2" data-testid="player-form-grad-year" />
              <p className="text-[10px] text-ink-400 mt-1">Used to group alumni by class on the Archive page.</p>
            </div>
          </div>
          <p className="eyebrow pt-4">Season Goals</p>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="eyebrow">PPG</Label>
              <Input value={form.ppg_goal} onChange={(e) => setForm({ ...form, ppg_goal: e.target.value })} inputMode="numeric" className="bg-ink-800 border-ink-700 mt-2" />
            </div>
            <div>
              <Label className="eyebrow">REB</Label>
              <Input value={form.reb_goal} onChange={(e) => setForm({ ...form, reb_goal: e.target.value })} inputMode="numeric" className="bg-ink-800 border-ink-700 mt-2" />
            </div>
            <div>
              <Label className="eyebrow">AST</Label>
              <Input value={form.ast_goal} onChange={(e) => setForm({ ...form, ast_goal: e.target.value })} inputMode="numeric" className="bg-ink-800 border-ink-700 mt-2" />
            </div>
          </div>
          <Button type="submit" className="w-full bg-burgundy hover:bg-burgundy-hover" data-testid="player-form-submit">
            {isNew ? "Add Player" : "Save Changes"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
